import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Weight, Ruler, Activity, CalendarDays, Pencil, Trash2 } from 'lucide-react';
import { formatDateFromIso } from '@/lib/datetime';

export default function MeasurementList({ measurements, onEdit, onDelete }) {
    if (measurements.length === 0) {
        return (
            <Card className="bg-[#0a0a0a] border-hairline-dark">
                <CardContent className="py-12 text-center">
                    <Activity className="h-12 w-12 text-white/60 mx-auto mb-4" />
                    <p className="text-white/60">No hay mediciones registradas.</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="grid gap-4">
            {measurements.map(m => (
                <Card key={m.id} className="bg-[#0a0a0a] border-hairline-dark">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <div className="flex items-center gap-3">
                            <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                                <CalendarDays className="h-4 w-4" />
                            </div>
                            <div>
                                <CardTitle className="text-sm font-semibold uppercase tracking-wide">
                                    {formatDateFromIso(m.measured_at)}
                                </CardTitle>
                                <p className="text-xs text-white/60">{m.measured_by || 'Sin profesional'}</p>
                            </div>
                        </div>
                        <div className="flex gap-1">
                            {onEdit && (
                                <Button variant="ghost" size="icon" onClick={() => onEdit(m)} className="text-white hover:bg-white/5 h-8 w-8">
                                    <Pencil className="h-4 w-4" />
                                </Button>
                            )}
                            {onDelete && (
                                <Button variant="ghost" size="icon" onClick={() => onDelete(m)} className="text-white hover:bg-white/5 h-8 w-8">
                                    <Trash2 className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 text-sm">
                            {m.weight && (
                                <div className="flex items-center gap-2">
                                    <Weight className="h-3 w-3 text-white/60" />
                                    <span className="text-white/60">Peso:</span>
                                    <span className="font-medium">{m.weight} kg</span>
                                </div>
                            )}
                            {m.height && (
                                <div className="flex items-center gap-2">
                                    <Ruler className="h-3 w-3 text-white/60" />
                                    <span className="text-white/60">Altura:</span>
                                    <span className="font-medium">{m.height} cm</span>
                                </div>
                            )}
                            {m.bmi && (
                                <div className="flex items-center gap-2">
                                    <Activity className="h-3 w-3 text-white/60" />
                                    <span className="text-white/60">IMC:</span>
                                    <span className="font-medium">{m.bmi}</span>
                                </div>
                            )}
                            {m.body_fat_percentage && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Grasa:</span>
                                    <span className="font-medium">{m.body_fat_percentage}%</span>
                                </div>
                            )}
                            {m.muscle_mass_kg && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Músculo:</span>
                                    <span className="font-medium">{m.muscle_mass_kg} kg</span>
                                </div>
                            )}
                            {m.muscle_mass_percentage && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">% Músculo:</span>
                                    <span className="font-medium">{m.muscle_mass_percentage}%</span>
                                </div>
                            )}
                            {m.bone_mass && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Masa ósea:</span>
                                    <span className="font-medium">{m.bone_mass} kg</span>
                                </div>
                            )}
                            {m.body_water_percentage && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Agua:</span>
                                    <span className="font-medium">{m.body_water_percentage}%</span>
                                </div>
                            )}
                            {m.visceral_fat && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Grasa visceral:</span>
                                    <span className="font-medium">{m.visceral_fat}</span>
                                </div>
                            )}
                            {m.basal_metabolism && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Metabolismo:</span>
                                    <span className="font-medium">{m.basal_metabolism} kcal</span>
                                </div>
                            )}
                            {m.chest && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Pecho:</span>
                                    <span className="font-medium">{m.chest} cm</span>
                                </div>
                            )}
                            {m.waist && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Cintura:</span>
                                    <span className="font-medium">{m.waist} cm</span>
                                </div>
                            )}
                            {m.abdomen && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Abdomen:</span>
                                    <span className="font-medium">{m.abdomen} cm</span>
                                </div>
                            )}
                            {m.hips && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Cadera:</span>
                                    <span className="font-medium">{m.hips} cm</span>
                                </div>
                            )}
                            {m.neck && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Cuello:</span>
                                    <span className="font-medium">{m.neck} cm</span>
                                </div>
                            )}
                            {m.right_arm && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Brazo der.:</span>
                                    <span className="font-medium">{m.right_arm} cm</span>
                                </div>
                            )}
                            {m.left_arm && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Brazo izq.:</span>
                                    <span className="font-medium">{m.left_arm} cm</span>
                                </div>
                            )}
                            {m.right_forearm && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Antebrazo der.:</span>
                                    <span className="font-medium">{m.right_forearm} cm</span>
                                </div>
                            )}
                            {m.left_forearm && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Antebrazo izq.:</span>
                                    <span className="font-medium">{m.left_forearm} cm</span>
                                </div>
                            )}
                            {m.right_thigh && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Muslo der.:</span>
                                    <span className="font-medium">{m.right_thigh} cm</span>
                                </div>
                            )}
                            {m.left_thigh && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Muslo izq.:</span>
                                    <span className="font-medium">{m.left_thigh} cm</span>
                                </div>
                            )}
                            {m.right_calf && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Pantorrilla der.:</span>
                                    <span className="font-medium">{m.right_calf} cm</span>
                                </div>
                            )}
                            {m.left_calf && (
                                <div className="flex items-center gap-2">
                                    <span className="text-white/60">Pantorrilla izq.:</span>
                                    <span className="font-medium">{m.left_calf} cm</span>
                                </div>
                            )}
                        </div>
                        {(m.chest || m.waist || m.abdomen || m.hips || m.neck || m.right_arm || m.left_arm || m.right_forearm || m.left_forearm || m.right_thigh || m.left_thigh || m.right_calf || m.left_calf) && (
                            <div className="mt-3 pt-3 border-t border-[#3a3a3f]">
                                <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Perímetros (cm)</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm">
                                    {m.chest && <span className="text-white/60">Pecho: <span className="text-white font-medium">{m.chest}</span></span>}
                                    {m.waist && <span className="text-white/60">Cintura: <span className="text-white font-medium">{m.waist}</span></span>}
                                    {m.abdomen && <span className="text-white/60">Abdomen: <span className="text-white font-medium">{m.abdomen}</span></span>}
                                    {m.hips && <span className="text-white/60">Cadera: <span className="text-white font-medium">{m.hips}</span></span>}
                                    {m.neck && <span className="text-white/60">Cuello: <span className="text-white font-medium">{m.neck}</span></span>}
                                    {m.right_arm && <span className="text-white/60">Brazo der.: <span className="text-white font-medium">{m.right_arm}</span></span>}
                                    {m.left_arm && <span className="text-white/60">Brazo izq.: <span className="text-white font-medium">{m.left_arm}</span></span>}
                                    {m.right_forearm && <span className="text-white/60">Antebrazo der.: <span className="text-white font-medium">{m.right_forearm}</span></span>}
                                    {m.left_forearm && <span className="text-white/60">Antebrazo izq.: <span className="text-white font-medium">{m.left_forearm}</span></span>}
                                    {m.right_thigh && <span className="text-white/60">Muslo der.: <span className="text-white font-medium">{m.right_thigh}</span></span>}
                                    {m.left_thigh && <span className="text-white/60">Muslo izq.: <span className="text-white font-medium">{m.left_thigh}</span></span>}
                                    {m.right_calf && <span className="text-white/60">Pantorrilla der.: <span className="text-white font-medium">{m.right_calf}</span></span>}
                                    {m.left_calf && <span className="text-white/60">Pantorrilla izq.: <span className="text-white font-medium">{m.left_calf}</span></span>}
                                </div>
                            </div>
                        )}
                        {m.observations && <p className="text-xs text-white/50 mt-2">{m.observations}</p>}
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
